<template>
  <el-dialog
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :title="$t('CenterForSorting.bulkImport')"
    :visible.sync="importDialog"
    custom-class="bulk-import-dialog"
    top="5vh"
    width="550px"
    @close="handleDialog"
  >
    <!-- 上传/结果 切换显示区域 -->
    <div v-if="!showResult">
      <div class="section-title">{{ $t('CenterForSorting.uploadRuleFile') }}</div>
      <!-- 上传区域 -->
      <div class="upload-section flx-center">
        <el-upload
          :auto-upload="false"
          :on-change="handleFileChange"
          :show-file-list="false"
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          action="#"
          class="upload-demo"
          drag
        >
          <div class="upload-content">
            <i class="el-icon-plus upload-icon" />
            <div class="upload-text">{{ $t('CenterForSorting.dragOrClickUpload') }}</div>
          </div>
        </el-upload>
      </div>
      <!-- 上传成功后的文件预览 -->
      <div v-if="fileName" class="file-preview">
        <span class="file-name">{{ fileName }}</span>
        <i class="el-icon-circle-close file-delete" @click="delFile" />
      </div>
      <!-- 模板下载区域 -->
      <div class="template-section">
        <div class="section-title">{{ $t('CenterForSorting.ruleTemplateDownload') }}</div>
        <div class="template-download" @click="downLoadFile">
          <span>{{ $t('CenterForSorting.downloadTemplate') }}</span>
          <i class="el-icon-download download-icon" />
        </div>
      </div>
    </div>

    <!-- 导入结果展示区域 -->
    <div v-else class="result-section">
      <div class="result-stats">
        <div class="stat-item">
          <span class="stat-label">{{ $t('CenterForSorting.totalRows') }}</span>
          <span class="stat-value">{{ resultData.totalRowCount }}</span>
        </div>
        <div class="stat-item success">
          <span class="stat-label">{{ $t('CenterForSorting.successCount') }}</span>
          <span class="stat-value">{{ resultData.successRowCount }}</span>
        </div>
        <div class="stat-item fail">
          <span class="stat-label">{{ $t('CenterForSorting.failCount') }}</span>
          <span class="stat-value">{{ resultData.failRowCount }}</span>
        </div>
      </div>

      <!-- 失败原因列表 -->
      <div v-if="resultData.failRowCount > 0" class="error-list">
        <div class="section-title">{{ $t('CenterForSorting.failReason') }}</div>
        <el-scrollbar class="error-scrollbar">
          <div v-for="(item, index) in resultData.errors" :key="index" class="error-item">
            <span class="error-msg">{{ item.message }}</span>
          </div>
        </el-scrollbar>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button class="btn-cancel" size="small" @click="handleDialog">
        {{ showResult ? $t('CenterForSorting.close') : $t('CenterForSorting.cancel') }}
      </el-button>
      <el-button
        v-if="!showResult"
        :loading="loading"
        class="btn-confirm"
        size="small"
        type="primary"
        @click="confirmImport"
      >{{ $t('CenterForSorting.confirmUpload') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { saveAs } from 'file-saver';
import { importRules } from '@/api/sorting/rule';

export default {
  name: 'BulkImport',
  data() {
    return {
      importDialog: false,
      fileName: '',
      fileData: null,
      sizeFile: '',
      ruleGroupId: '',
      loading: false,
      showResult: false, // 是否显示结果页
      resultData: {
        // 存储后端返回的结果数据
        errors: [],
        failRowCount: 0,
        successRowCount: 0,
        successRuleCount: 0,
        totalRowCount: 0
      }
    };
  },
  methods: {
    // 控制弹框显示
    handleOpen(id) {
      this.ruleGroupId = id;
      this.importDialog = true;
      // 每次打开时重置结果状态
      this.showResult = false;
      this.resultData = {
        errors: [],
        failRowCount: 0,
        successRowCount: 0,
        successRuleCount: 0,
        totalRowCount: 0
      };
    },
    // 处理文件选择
    handleFileChange(file) {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        this.$message.error(this.$t('CenterForSorting.fileSizeLimit'));
        return;
      }

      // 验证文件类型
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(extension)) {
        this.$message.error(this.$t('CenterForSorting.invalidFileType'));
        return;
      }

      // 清空上次的数据，防止残渣
      this.delFile();
      this.fileData = file.raw;
      this.fileName = file.name;
      this.sizeFile = file.size;
    },
    // 删除已选文件
    delFile() {
      this.fileName = '';
      this.fileData = null;
      this.sizeFile = '';
    },
    // 关闭弹窗
    handleDialog() {
      const wasSuccess = this.showResult && this.resultData.successRowCount > 0;
      this.delFile();
      this.importDialog = false;
      // 延迟重置状态，避免关闭动画时闪现
      setTimeout(() => {
        this.showResult = false;
        this.resultData = {
          errors: [],
          failRowCount: 0,
          successRowCount: 0,
          successRuleCount: 0,
          totalRowCount: 0
        };
      }, 200);
      // 只在成功导入后触发刷新事件
      if (wasSuccess) {
        this.$emit('import-success');
      }
    },
    // 确认上传
    confirmImport() {
      if (!this.fileName) {
        this.$message.warning(this.$t('CenterForSorting.pleaseSelectFile'));
        return;
      }
      if (this.loading) return; // 防止重复提交
      this.loading = true;
      const formData = new FormData();
      formData.append('sizeFile', this.sizeFile);
      formData.append('file', this.fileData);
      formData.append('fileName', this.fileName);
      formData.append('ruleGroupId', this.ruleGroupId);

      importRules(formData)
        .then((res) => {
          this.loading = false;
          // 处理后端返回的 data
          if (res.data) {
            this.resultData = res.data;
            this.showResult = true;
          } else {
            // 如果没有返回 data，按极端情况处理或默认全部成功
            this.$message.success(this.$t('CenterForSorting.importSuccess'));
            this.importDialog = false;
            this.$emit('import-success');
          }
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
          // this.$message.error(this.$t('CenterForSorting.importFailed') + (error.message || ''));
        });
    },
    // 下载模板
    async downLoadFile() {
      try {
        const response = await fetch('/static/批量导入规则模板.xlsx');
        if (!response.ok) {
          throw new Error('Template file not found');
        }
        const blob = await response.blob();
        saveAs(blob, '批量导入规则模板.xlsx');
      } catch (error) {
        this.$message.error(this.$t('CenterForSorting.templateDownloadFailed'));
      }
    }
  }
};
</script>

<style scoped>
/* 覆盖 el-upload 外层容器 */
.upload-demo :deep(.el-upload) {
  width: 100%;
}

/* 1. 重点：让拖拽区完全按照我们定义的 180px 高度填充，去掉默认 padding */
.upload-demo :deep(.el-upload-dragger) {
  width: 100%;
  height: 180px !important;
  padding: 0 !important;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  background-color: #fafafa;

  /* 使用 flex 让内部所有东西完美居中 */
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
}

/* 2. 隐藏 Element UI 默认的文字节点（因为它干扰了我们的 flex 布局） */
.upload-demo :deep(.el-upload__text) {
  display: none !important;
}

/* 3. 设置我们的自定义内容容器 */
.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* 加号图标 */
.upload-icon {
  font-size: 40px;
  color: #a8abb2;
  margin-bottom: 12px;
  line-height: 1;
}

/* 提示文字 */
.upload-text {
  font-size: 14px;
  color: #606266;
  line-height: 1;
}

/* 其他样式保持不变... */
.upload-demo :deep(.el-upload-dragger:hover) {
  border-color: #00a551;
  background-color: #f5f9f7;
}

.file-preview {
  margin-top: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.file-name {
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 85%;
}

.file-delete {
  cursor: pointer;
  color: #f56c6c;
  font-size: 16px;
}

.template-section {
  margin: 10px 0;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 12px;
}

.template-download {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.template-download:hover {
  background-color: #e6f7d9;
}

.template-download span {
  font-size: 14px;
  color: #00a551;
  font-weight: 500;
}

.download-icon {
  color: #00a551;
  font-size: 18px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel {
  border-color: #dcdfe6;
  color: #606266;
}

.btn-confirm {
  background-color: #00a551;
  border-color: #00a551;
}

.btn-confirm:hover,
.btn-confirm:focus {
  background-color: #008f45;
  border-color: #008f45;
}

/* ================== 新增：结果展示区域样式 ================== */
.result-section {
  padding: 10px 0;
}

.result-stats {
  display: flex;
  justify-content: space-between;
  background-color: #f5f7fa;
  border-radius: 6px;
  padding: 15px 20px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
}

.stat-label {
  font-size: 14px;
  color: #606266;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-left: 5px;
}

.stat-item.success .stat-value {
  color: #67c23a;
}

.stat-item.fail .stat-value {
  color: #f56c6c;
}

.error-list {
  border: 1px solid #fde2e2;
  background-color: #fef0f0;
  border-radius: 6px;
  padding: 12px;
}

.error-list .section-title {
  color: #f56c6c;
  font-size: 14px;
  margin-bottom: 8px;
}

.error-scrollbar {
  max-height: 200px;
}

.error-item {
  display: flex;
  font-size: 13px;
  line-height: 1.6;
  padding: 4px 0;
  color: #606266;
  border-bottom: 1px dashed #fde2e2;
}

.error-item:last-child {
  border-bottom: none;
}

.error-row {
  color: #f56c6c;
  font-weight: bold;
  flex-shrink: 0;
  margin-right: 8px;
}

.error-msg {
  word-break: break-all;
}
</style>
